import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import ResultTable from './ResultTable';
import ResultChart from './ResultChart';
import InlineNotice from './InlineNotice';
import SuggestionChips from './SuggestionChips';
import { toMessageList, pickDatasetFields } from '../utils/datasetSummary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy, faFaceFrown, faRobot, faUser } from '@fortawesome/free-solid-svg-icons';

function parseContent(content) {
  if (!content || typeof content !== 'string') return null;
  try {
    const parsed = JSON.parse(content);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

// This is the single point that turns a raw backend message/payload into
// the shape every section below renders from. Every field is read
// defensively (flat, then `metadata`) so a new backend field just works
// without touching this function; unknown `response_type` values fall
// through to the same section-by-section rendering as known ones.
function normalizeAssistantPayload(message) {
  const parsed = parseContent(message.content);
  const payload = parsed || (message && typeof message === 'object' ? message : null);
  if (!payload || typeof payload !== 'object') return null;

  const meta = payload.metadata && typeof payload.metadata === 'object' ? payload.metadata : {};
  const pick = (...keys) => {
    for (const key of keys) {
      if (payload[key] !== undefined) return payload[key];
      if (meta[key] !== undefined) return meta[key];
    }
    return undefined;
  };

  const responseType = String(pick('response_type', 'type') ?? 'summary').toLowerCase();
  const summary = pick('summary', 'message', 'answer', 'text', 'content') ?? '';
  const rawTable = pick('table', 'result');
  const tableData = Array.isArray(rawTable) ? rawTable : [];
  const rawChart = pick('chart');
  const chart = rawChart && typeof rawChart === 'object' ? rawChart : null;
  const emptyMessage = pick('empty_message', 'emptyMessage') || 'No matching records were found. Try another search term or adjust your filters.';
  const errorMessage = pick('error_message', 'errorMessage', 'message') || 'Sorry, something went wrong while processing your request. Please try again.';
  const rawDatasetSummary = pick('dataset_summary', 'datasetSummary');
  const suggestedQuestions = pick('suggested_questions', 'suggestedQuestions');

  return {
    ...payload,
    responseType,
    summary,
    tableData,
    chart,
    emptyMessage,
    errorMessage,
    datasetSummary: rawDatasetSummary && typeof rawDatasetSummary === 'object' ? rawDatasetSummary : null,
    validation: toMessageList(pick('validation')),
    information: toMessageList(pick('information')),
    warnings: toMessageList(pick('warnings')),
    security: toMessageList(pick('security')),
    suggestedQuestions: Array.isArray(suggestedQuestions)
      ? suggestedQuestions.filter(q => typeof q === 'string' && q.trim())
      : [],
    paginationEnabled: Boolean(pick('pagination_enabled')),
    page: Number.isFinite(payload.page) ? payload.page : Number.isFinite(meta.page) ? meta.page : 0,
    totalPages: Number.isFinite(payload.total_pages) ? payload.total_pages : Number.isFinite(meta.total_pages) ? meta.total_pages : 1,
    query: pick('query'),
  };
}

function tableToText(rows) {
  if (!Array.isArray(rows) || !rows.length) return '';
  const cols = Object.keys(rows[0] || {});
  const header = cols.join('\t');
  const body = rows.map(row => cols.map(col => String(row?.[col] ?? '')).join('\t')).join('\n');
  return [header, body].filter(Boolean).join('\n');
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (!text) return;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <button type="button" onClick={copy} style={s.copyBtn} aria-label="Copy table">
      <FontAwesomeIcon icon={copied ? faCheck : faCopy} aria-hidden="true" /> {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// Labels the backend's own dataset_summary values (rows/columns/worksheets/
// quality/detected types/recommendations) — no summary text is authored
// here, only unit labels for whatever numbers/lists the backend sent.
function DatasetSummaryBlock({ ds }) {
  const fields = pickDatasetFields(ds);
  if (!fields) return null;
  const { name, rows, columns, worksheets, quality, detectedTypes, recommendations } = fields;

  const stats = [
    name ? String(name) : null,
    rows != null ? `${Number(rows).toLocaleString()} rows` : null,
    columns != null ? `${columns} columns` : null,
    worksheets != null ? `${worksheets} worksheet${worksheets === 1 ? '' : 's'}` : null,
    quality != null ? `quality ${quality}` : null,
  ].filter(Boolean);

  const typeEntries = detectedTypes && typeof detectedTypes === 'object' && !Array.isArray(detectedTypes)
    ? Object.entries(detectedTypes)
    : [];

  if (!stats.length && !typeEntries.length && !recommendations.length) return null;

  return (
    <div style={s.datasetBlock}>
      {stats.length > 0 && (
        <div style={s.statRow}>
          {stats.map((stat, i) => <span key={i} style={s.statChip}>{stat}</span>)}
        </div>
      )}
      {typeEntries.length > 0 && (
        <div style={s.statRow}>
          {typeEntries.map(([col, type]) => (
            <span key={col} style={s.typeChip}>{col}: {String(type)}</span>
          ))}
        </div>
      )}
      {recommendations.length > 0 && (
        <ul style={s.list}>
          {recommendations.map((r, i) => <li key={i} style={s.listItem}>{r}</li>)}
        </ul>
      )}
    </div>
  );
}

// Single unified renderer: every assistant message — chat, relation, or an
// upload response reshaped into a message envelope — passes through here.
// Sections render whenever their data is present, independent of
// response_type, so an unrecognized response_type still shows its summary/
// table/chart/notices/suggestions instead of being silently dropped.
function AssistantContent({ ai, onSuggest }) {
  const visibleText = useMemo(() => (typeof ai?.summary === 'string' ? ai.summary.trim() : ''), [ai]);

  if (!ai) {
    return <p style={s.aiText}>I&apos;m here to help.</p>;
  }

  if (ai.responseType === 'error') {
    return (
      <div style={s.statusCardError}>
        <div style={s.statusIconError}>
          <FontAwesomeIcon icon={faFaceFrown} aria-hidden="true" />
        </div>
        <div>
          <div style={s.statusTitle}>Something went wrong</div>
          <div style={s.statusText}>{ai.errorMessage}</div>
        </div>
      </div>
    );
  }

  if (ai.responseType === 'empty') {
    return (
      <div style={s.statusCardEmpty}>
        <div>
          <div style={s.statusTitle}>No results found</div>
          <div style={s.statusText}>{ai.emptyMessage}</div>
        </div>
      </div>
    );
  }

  const hasTable = ai.tableData?.length > 0;
  const hasChart = Boolean(ai.chart?.type) && ai.chart.type !== 'none' && hasTable;
  const notices = [
    ...ai.security.map(text => ({ variant: 'security', text })),
    ...ai.warnings.map(text => ({ variant: 'warning', text })),
    ...ai.information.map(text => ({ variant: 'information', text })),
  ];

  return (
    <>
      {visibleText ? (
        <div style={s.summary}>
          <ReactMarkdown
            components={{
              p: ({ children }) => <p style={s.aiText}>{children}</p>,
              ul: ({ children }) => <ul style={s.list}>{children}</ul>,
              ol: ({ children }) => <ol style={s.list}>{children}</ol>,
              li: ({ children }) => <li style={s.listItem}>{children}</li>,
              strong: ({ children }) => <strong style={s.strong}>{children}</strong>,
            }}
          >
            {visibleText}
          </ReactMarkdown>
        </div>
      ) : null}

      <DatasetSummaryBlock ds={ai.datasetSummary} />

      {ai.validation.length > 0 && (
        <ul style={s.list}>
          {ai.validation.map((v, i) => <li key={i} style={s.listItem}>{v}</li>)}
        </ul>
      )}

      {notices.length > 0 && (
        <div style={s.noticeStack}>
          {notices.map((n, i) => <InlineNotice key={i} variant={n.variant} text={n.text} />)}
        </div>
      )}

      {hasTable && (
        <>
          <div style={s.spacer} />
          <ResultTable data={ai.tableData} />
        </>
      )}

      {hasChart && (
        <>
          <div style={s.spacer} />
          <ResultChart chart={ai.chart} data={ai.tableData} />
        </>
      )}

      <SuggestionChips questions={ai.suggestedQuestions} onSuggest={onSuggest} />
    </>
  );
}

export default function MessageBubble({ message, isLast, onPaginate, onSuggest }) {
  const isUser = message.role === 'user';
  const ai = !isUser ? normalizeAssistantPayload(message) : null;
  const [jumpPage, setJumpPage] = useState((ai?.page ?? 0) + 1);

  useEffect(() => {
    setJumpPage((ai?.page ?? 0) + 1);
  }, [ai?.page, ai?.totalPages]);

  const canPaginate = !!ai?.paginationEnabled && ai?.totalPages > 1 && typeof onPaginate === 'function';
  const currentPage = (ai?.page ?? 0) + 1;
  const totalPages = ai?.totalPages ?? 1;
  const hasTable = !isUser && ai?.tableData?.length > 0;

  const goToPage = (nextPage) => {
    if (!ai?.query) return;
    if (Number.isNaN(nextPage)) return;
    const target = Math.min(Math.max(nextPage, 0), Math.max(0, totalPages - 1));
    onPaginate(ai.query, target);
  };


  return (
    <div style={{ ...s.wrap, ...(isUser ? s.wrapUser : s.wrapAssistant), animation: isLast ? 'fadeIn 0.3s ease' : 'none' }}>
      <div style={{ ...s.avatar, ...(isUser ? s.avatarUser : s.avatarAI) }}>
        <FontAwesomeIcon icon={isUser ? faUser : faRobot} aria-hidden="true" />
      </div>

      <div style={{ ...s.bubble, ...(isUser ? s.bubbleUser : s.bubbleAI) }}>
        {isUser ? (
          <p style={s.userText}>{message.content}</p>
        ) : (
          <AssistantContent ai={ai} onSuggest={onSuggest} />
        )}

        {hasTable && (
          <div style={s.copyRow}>
            <CopyButton text={tableToText(ai.tableData)} />
          </div>
        )}

        {canPaginate && (
          <div style={s.paginationNote}>
            <span style={s.paginationText}>
              Showing page {currentPage} of {totalPages}
            </span>
            <div style={s.paginationActions}>
              <button
                type="button"
                onClick={() => goToPage((ai?.page ?? 0) - 1)}
                disabled={(ai?.page ?? 0) <= 0}
                style={s.pageBtn}
              >
                Previous
              </button>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={jumpPage}
                onChange={e => setJumpPage(e.target.value)}
                style={s.pageInput}
              />
              <button
                type="button"
                onClick={() => goToPage(Number(jumpPage) - 1)}
                style={s.pageBtn}
              >
                Go
              </button>
              <button
                type="button"
                onClick={() => goToPage((ai?.page ?? 0) + 1)}
                disabled={(ai?.page ?? 0) >= totalPages - 1}
                style={s.pageBtn}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  wrap: { display: 'flex', gap: 12, marginBottom: 20, maxWidth: '100%', alignItems: 'flex-start' },
  wrapUser: { flexDirection: 'row-reverse' },
  wrapAssistant: { flexDirection: 'row', alignItems: 'flex-start' },
  avatar: {
    width: 34, height: 34, borderRadius: 12, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 14, flexShrink: 0, marginTop: 2,
  },
  avatarUser: { background: 'var(--accent)', color: '#fff' },
  avatarAI: { background: 'var(--bg-tertiary)', color: 'var(--accent)', fontWeight: 700 },
  bubble: {
    maxWidth: 'min(760px, calc(100% - 56px))',
    borderRadius: 18,
    padding: '10px 16px',
    position: 'relative',
  },
  bubbleUser: { background: 'var(--accent)', color: '#fff', borderBottomRightRadius: 6 },
  bubbleAI: { background: 'var(--bg-secondary)', borderBottomLeftRadius: 6 },
  userText: {
    fontSize: 14,
    lineHeight: 1.65,
    margin: 0,
    cursor: 'text',
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    msUserSelect: 'text',
    whiteSpace: 'pre-wrap',
  },
  summary: { marginBottom: 0 },
  aiText: {
    fontSize: 14,
    lineHeight: 1.75,
    color: 'var(--text-primary)',
    margin: 0,
    cursor: 'text',
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    msUserSelect: 'text',
    whiteSpace: 'pre-wrap',
  },
  strong: { fontWeight: 700 },
  list: { margin: '8px 0 0 18px', padding: 0 },
  listItem: { marginBottom: 4, lineHeight: 1.6 },
  copyBtn: {
    flexShrink: 0,
    fontSize: 11,
    color: 'var(--text-muted)',
    background: 'transparent',
    border: '1px solid var(--border-subtle)',
    cursor: 'pointer',
    fontWeight: 600,
    padding: '6px 10px',
    borderRadius: 999,
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },
  copyRow: { display: 'flex', justifyContent: 'flex-end', marginTop: 10 },
  spacer: { height: 8 },
  datasetBlock: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 },
  statRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  statChip: {
    fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
    background: 'var(--bg-tertiary)', padding: '4px 10px', borderRadius: 999,
  },
  typeChip: {
    fontSize: 11, color: 'var(--text-muted)',
    background: 'var(--bg-primary)', padding: '3px 9px', borderRadius: 999,
    border: '1px solid var(--border-subtle)',
  },
  noticeStack: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 },
  statusCardError: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 14,
    background: 'var(--error-subtle)',
  },
  statusIconError: {
    width: 32, height: 32, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(239, 68, 68, 0.12)', color: '#dc2626', flexShrink: 0,
  },
  statusCardEmpty: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 14,
    background: 'var(--bg-tertiary)',
  },
  statusTitle: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 },
  statusText: { fontSize: 13, lineHeight: 1.65, color: 'var(--text-secondary)' },
  paginationNote: {
    display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
    justifyContent: 'space-between', padding: '8px 12px', borderRadius: 10,
    background: 'var(--bg-tertiary)', marginTop: 10,
  },
  paginationText: { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 },
  paginationActions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  pageBtn: {
    padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
    cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  pageInput: {
    width: 64, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border-subtle)',
    background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 12,
    fontFamily: 'var(--font-main)',
  },
};
